import React, { useEffect, useState } from "react";
import { fetchJson } from "../../api";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.15)";
const GOLD_BORDER = "rgba(212,165,80,0.35)";
const SURFACE = "#12121a";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

function Avatar({ nome, cognome, url, size = 28 }) {
  const initials = `${(nome||"")[0]||""}${(cognome||"")[0]||""}`.toUpperCase();
  if (url) return <img src={url} alt={initials} style={{ width:size,height:size,borderRadius:"50%",objectFit:"cover",flexShrink:0 }} />;
  return <div style={{ width:size,height:size,borderRadius:"50%",background:GOLD_DIM,border:`1px solid ${GOLD_BORDER}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.38,fontWeight:700,color:GOLD,flexShrink:0 }}>{initials||"?"}</div>;
}

function SectionTitle({ children }) {
  return <div style={{ fontSize:11,color:GOLD,textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700,marginBottom:10 }}>{children}</div>;
}

export default function AddLineModal({ planId, productionId, planStart, planEnd, lanes, episodes, onClose, onSaved }) {
  const [tipo, setTipo] = useState("integrata");

  // ---- Risorsa picker ----
  const [resourceMode, setResourceMode] = useState("search");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [results, setResults] = useState([]);
  const [ruoliDisponibili, setRuoliDisponibili] = useState([]);
  const [selected, setSelected] = useState(null);
  const [manual, setManual] = useState("");
  const [rosaItems, setRosaItems] = useState([]);

  useEffect(() => {
    fetchJson("/postplans/freelancers/roles").then(d => setRuoliDisponibili(d.roles || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (resourceMode !== "search") return;
    const t = setTimeout(async () => {
      try {
        const fromParam = planStart ? `&from=${encodeURIComponent(planStart)}` : "";
        const toParam = planEnd ? `&to=${encodeURIComponent(planEnd)}` : "";
        const data = await fetchJson(`/postplans/freelancers/search?q=${encodeURIComponent(q)}&role=${encodeURIComponent(role)}${fromParam}${toParam}`);
        setResults(data.freelancers || []);
      } catch (_) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, role, resourceMode, planStart, planEnd]);

  useEffect(() => {
    if (resourceMode !== "rosa" || !productionId) return;
    const fromParam = planStart ? `&from=${encodeURIComponent(planStart)}` : "";
    const toParam = planEnd ? `&to=${encodeURIComponent(planEnd)}` : "";
    fetchJson(`/postplans/rosa/${productionId}?${fromParam}${toParam}`)
      .then(d => setRosaItems(d.rosa || [])).catch(() => setRosaItems([]));
  }, [resourceMode, productionId, planStart, planEnd]);

  // ---- Campi ----
  const [phaseId, setPhaseId] = useState("");
  const [intFromDate, setIntFromDate] = useState("");
  const [intToDate, setIntToDate] = useState("");
  const [speedMultiplier, setSpeedMultiplier] = useState("");

  const [indFromDate, setIndFromDate] = useState("");
  const [indToDate, setIndToDate] = useState("");
  const [partLabel, setPartLabel] = useState("");

  const MILESTONE_KEYS = new Set(["vis_autore","mod_autore","vis_rete","mod_rete","master","independent"]);
  const availablePhases = (lanes || []).reduce((acc, l) => {
    if (acc.find(p => p.phaseId === l.phaseId)) return acc;
    if (MILESTONE_KEYS.has(l.phaseTypeKey)) return acc;
    acc.push({ phaseId: l.phaseId, phaseName: l.phaseName, phaseTypeKey: l.phaseTypeKey });
    return acc;
  }, []);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const resourceLabel = resourceMode === "manual" ? manual.trim() : (selected ? `${selected.nome||""} ${selected.cognome||""}`.trim() : "");
  const resourceFreelancerId = resourceMode !== "manual" && selected ? (selected.freelancer_id || selected.id) : null;
  const hasResource = resourceMode === "manual" ? !!manual.trim() : !!selected;

  const canSubmit = (() => {
    if (tipo === "integrata") return !!phaseId && !!intFromDate;
    if (tipo === "indipendente") return !!partLabel.trim() && !!indFromDate && !!indToDate;
    return false;
  })();

  async function handleSubmit() {
    setErr("");
    setSaving(true);
    try {
      if (tipo === "integrata") {
        const payload = {
          phaseId: Number(phaseId),
          availableFrom: intFromDate,
          availableTo: intToDate || null,
          label: resourceLabel || null,
          speedMultiplier: speedMultiplier ? Number(speedMultiplier) : null,
          freelancerId: resourceFreelancerId,
        };
        await fetchJson(`/postplans/${planId}/lines/add`, { method: "POST", body: JSON.stringify(payload) });
      } else {
        // Tipo indipendente: partLabel = etichetta blocco (obbligatoria), label persona = opzionale
        const payload = {
          label: partLabel.trim(),  // etichetta blocco/attività
          personLabel: resourceLabel || null,  // persona, opzionale
          from: indFromDate,
          to: indToDate,
          freelancerId: resourceFreelancerId,
        };
        await fetchJson(`/postplans/${planId}/lines/independent`, { method: "POST", body: JSON.stringify(payload) });
      }
      onSaved && onSaved();
    } catch (e) {
      setErr(e.message || "Errore inatteso");
    } finally {
      setSaving(false);
    }
  }

  const inputBase = { background:"rgba(255,255,255,0.04)", border:`1px solid ${BORDER}`, borderRadius:6, color:TEXT, fontSize:13, padding:"7px 10px", outline:"none", boxSizing:"border-box", width:"100%" };
  const labelBase = { fontSize:11, color:TEXT_DIM, textTransform:"uppercase", letterSpacing:"0.06em", display:"block", marginBottom:4 };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:10,width:540,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden" }}>

        {/* Header */}
        <div style={{ padding:"14px 20px",borderBottom:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div style={{ fontSize:14,fontWeight:700,color:TEXT }}>+ Linea</div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:TEXT_DIM,cursor:"pointer",fontSize:18,padding:0 }}>×</button>
        </div>

        {/* Body scrollable */}
        <div style={{ flex:1,overflow:"auto",scrollbarWidth:"thin",scrollbarColor:"rgba(212,165,80,0.4) transparent" }}>

          {/* TIPO */}
          <div style={{ padding:"16px 20px",borderBottom:`1px solid ${BORDER}` }}>
            <SectionTitle>Tipo di linea</SectionTitle>
            <div style={{ display:"flex",gap:10 }}>
              {[
                { k:"integrata", titolo:"Integrata", desc:"Si unisce al flusso, riceve episodi" },
                { k:"indipendente", titolo:"Indipendente", desc:"Periodo libero, fuori dal calcolo" },
              ].map(opt => (
                <button key={opt.k} onClick={()=>setTipo(opt.k)} style={{
                  flex:1,padding:"10px 12px",borderRadius:8,cursor:"pointer",textAlign:"left",
                  background: tipo===opt.k ? "rgba(212,165,80,0.10)" : "transparent",
                  border: `1px solid ${tipo===opt.k ? GOLD_BORDER : BORDER}`,
                  color: tipo===opt.k ? GOLD : TEXT,
                }}>
                  <div style={{ fontSize:13,fontWeight:600 }}>{opt.titolo}</div>
                  <div style={{ fontSize:11,color:TEXT_DIM,marginTop:2 }}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* DETTAGLI */}
          <div style={{ padding:"16px 20px",borderBottom:`1px solid ${BORDER}` }}>
            <SectionTitle>Dettagli</SectionTitle>

            {tipo === "integrata" && (
              <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                <div>
                  <label style={labelBase}>Fase *</label>
                  <select value={phaseId} onChange={e=>setPhaseId(e.target.value)} style={{...inputBase, background:"#1a1a24", appearance:"auto"}}>
                    <option value="">— Scegli fase —</option>
                    {availablePhases.map(p => <option key={p.phaseId} value={p.phaseId}>{p.phaseName}</option>)}
                  </select>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                  <div>
                    <label style={labelBase}>Disponibile dal *</label>
                    <input type="date" value={intFromDate} onChange={e=>setIntFromDate(e.target.value)} style={{...inputBase, colorScheme:"dark"}} />
                  </div>
                  <div>
                    <label style={labelBase}>Disponibile fino al</label>
                    <input type="date" value={intToDate} onChange={e=>setIntToDate(e.target.value)} style={{...inputBase, colorScheme:"dark"}} />
                  </div>
                </div>
                <div>
                  <label style={labelBase}>Velocità (turni/ep)</label>
                  <input type="number" min="1" placeholder="default fase" value={speedMultiplier} onChange={e=>setSpeedMultiplier(e.target.value)} style={inputBase} />
                </div>
              </div>
            )}

            {tipo === "indipendente" && (
              <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
                <div>
                  <label style={labelBase}>Etichetta blocco *</label>
                  <input value={partLabel} onChange={e=>setPartLabel(e.target.value)} placeholder="Es. Montaggio schede web" style={inputBase} />
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                  <div>
                    <label style={labelBase}>Dal *</label>
                    <input type="date" value={indFromDate} onChange={e=>setIndFromDate(e.target.value)} style={{...inputBase, colorScheme:"dark"}} />
                  </div>
                  <div>
                    <label style={labelBase}>Al *</label>
                    <input type="date" value={indToDate} onChange={e=>setIndToDate(e.target.value)} style={{...inputBase, colorScheme:"dark"}} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RISORSA */}
          <div style={{ padding:"16px 20px" }}>
            <SectionTitle>Risorsa (opzionale)</SectionTitle>
            <div style={{ display:"flex",borderBottom:`1px solid ${BORDER}`,marginBottom:10 }}>
              {[["search","Cerca su JKL"],["rosa","Dalla rosa"],["manual","Nome manuale"]].map(([k,l])=>(
                <button key={k} onClick={()=>{setResourceMode(k);setSelected(null);}} style={{ flex:1,padding:"7px",fontSize:12,fontWeight:600,background:"none",border:"none",borderBottom:`2px solid ${resourceMode===k?GOLD:"transparent"}`,color:resourceMode===k?GOLD:TEXT_DIM,cursor:"pointer" }}>{l}</button>
              ))}
            </div>

            {resourceMode === "search" && (
              <div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:8 }}>
                  {ruoliDisponibili.slice(0,8).map(r=>(<button key={r} onClick={()=>setRole(role===r?'':r)} style={{ padding:"3px 8px",borderRadius:12,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${role===r?GOLD_BORDER:BORDER}`,background:role===r?GOLD_DIM:"transparent",color:role===r?GOLD:TEXT_DIM }}>{r}</button>))}
                </div>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca per nome…" style={{...inputBase, marginBottom:6}} />
                <div style={{ maxHeight:180,overflow:"auto",border:`1px solid ${BORDER}`,borderRadius:6 }}>
                  {results.map(f=>{
                    const isSel = selected?.id===f.id;
                    return (
                      <div key={f.id} onClick={()=>setSelected(isSel?null:f)} style={{ padding:"8px 10px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",borderBottom:`1px solid ${BORDER}`,background:isSel?"rgba(212,165,80,0.08)":"transparent" }}>
                        <Avatar nome={f.nome} cognome={f.cognome} url={f.profile_image_url||f.avatar_url} />
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ fontSize:13,fontWeight:600,color:TEXT,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{f.nome} {f.cognome}</div>
                          {f.ruoli?.length>0 && <div style={{ fontSize:11,color:TEXT_DIM,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{f.ruoli.slice(0,3).join(" · ")}</div>}
                        </div>
                        {isSel && <span style={{ fontSize:12,color:GOLD }}>✓</span>}
                      </div>
                    );
                  })}
                  {results.length===0 && q && <div style={{ padding:14,textAlign:"center",color:TEXT_DIM,fontSize:12 }}>Nessun risultato</div>}
                </div>
              </div>
            )}

            {resourceMode === "rosa" && (
              <div style={{ maxHeight:200,overflow:"auto",border:`1px solid ${BORDER}`,borderRadius:6 }}>
                {rosaItems.length === 0 && <div style={{ padding:14,textAlign:"center",color:TEXT_DIM,fontSize:12 }}>Rosa vuota</div>}
                {rosaItems.map(r => {
                  const nome = r.nome ? `${r.nome} ${r.cognome||""}`.trim() : r.name_override;
                  const isSel = selected?.id === r.id;
                  return (
                    <div key={r.id} onClick={()=>setSelected(isSel?null:r)} style={{ padding:"8px 10px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",borderBottom:`1px solid ${BORDER}`,background:isSel?"rgba(212,165,80,0.08)":"transparent" }}>
                      <Avatar nome={r.nome} cognome={r.cognome} url={r.profile_image_url||r.avatar_url} />
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:13,fontWeight:600,color:TEXT,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{nome}</div>
                        <div style={{ fontSize:11,color:TEXT_DIM }}>{(r.ruoli||[])[0]||r.ruolo||"—"}</div>
                      </div>
                      {isSel && <span style={{ color:GOLD,fontSize:13 }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {resourceMode === "manual" && (
              <input value={manual} onChange={e=>setManual(e.target.value)} placeholder="Es. Mario Rossi" style={inputBase} autoFocus />
            )}
          </div>

          {err && <div style={{ margin:"0 20px 14px 20px",fontSize:12,color:"#f87171",padding:"6px 10px",background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:6 }}>{err}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 20px",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"flex-end",gap:8 }}>
          <button onClick={onClose} style={{ background:"none",border:`1px solid ${BORDER}`,borderRadius:6,color:TEXT_DIM,padding:"8px 18px",cursor:"pointer",fontSize:13 }}>Annulla</button>
          <button disabled={!canSubmit||saving} onClick={handleSubmit}
            style={{ background:"linear-gradient(135deg,#d4a550,#b8892f)",border:"none",borderRadius:6,color:"#0a0a0f",padding:"8px 20px",cursor:(!canSubmit||saving)?"not-allowed":"pointer",fontSize:13,fontWeight:800,opacity:(!canSubmit||saving)?0.4:1 }}>
            {saving ? "Aggiunta…" : "Aggiungi"}
          </button>
        </div>
      </div>
    </div>
  );
}
